import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Image, Video, Lock, Globe, Users, Trash2, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { AdminUser } from '@/services/userManagementService';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface UserPostsViewerProps {
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
}

interface UserPost {
  id: string;
  caption: string | null;
  post_type: string;
  privacy_setting: string;
  created_at: string;
  pet_id: string;
  media_files: {
    id: string;
    url: string;
    media_type: string;
  }[];
  pets: {
    name: string;
    photo_url: string | null;
  } | null;
}

const UserPostsViewer: React.FC<UserPostsViewerProps> = ({ user, isOpen, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [postToDelete, setPostToDelete] = useState<UserPost | null>(null);
  const [deleteReason, setDeleteReason] = useState('');

  // Fetch user posts
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['admin-user-posts', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('memory_posts')
        .select(`
          id,
          caption,
          post_type,
          privacy_setting,
          created_at,
          pet_id,
          media_files (
            id,
            url,
            media_type
          ),
          pets (
            name,
            photo_url
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Deduplicate posts with same media URL created within 60 seconds
      const seen = new Map<string, number>();
      const deduplicated = (data as UserPost[]).filter((post) => {
        const url = post.media_files?.[0]?.url;
        if (!url) return true;
        const ts = new Date(post.created_at).getTime();
        const prevTs = seen.get(url);
        if (prevTs !== undefined && Math.abs(ts - prevTs) < 60000) return false;
        seen.set(url, ts);
        return true;
      });
      
      return deduplicated;
    },
    enabled: isOpen && !!user?.id,
  });

  // Delete post mutation
  const deletePostMutation = useMutation({
    mutationFn: async ({ postId, reason }: { postId: string; reason: string }) => {
      // First, log the action in admin audit log
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        await supabase.from('admin_audit_log').insert({
          admin_user_id: userData.user.id,
          action: 'delete_post',
          target_user_id: user?.id,
          details: { post_id: postId, reason }
        });
      }

      // Delete media files first
      const { error: mediaError } = await supabase
        .from('media_files')
        .delete()
        .eq('post_id', postId);

      if (mediaError) throw mediaError;

      // Delete the post
      const { error } = await supabase
        .from('memory_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user-posts', user?.id] });
      toast.success(t('adminPanel.userPosts.postDeletedSuccess'));
      setPostToDelete(null);
      setDeleteReason('');
    },
    onError: (error) => {
      console.error('Error deleting post:', error);
      toast.error(t('adminPanel.moderation.postDeleteError'));
    },
  });

  const getPrivacyIcon = (setting: string) => {
    switch (setting) {
      case 'private':
        return <Lock className="h-3 w-3" />;
      case 'followers':
        return <Users className="h-3 w-3" />;
      default:
        return <Globe className="h-3 w-3" />;
    }
  };

  const getPrivacyLabel = (setting: string) => {
    switch (setting) {
      case 'private':
        return t('adminPanel.userPosts.private');
      case 'followers':
        return t('adminPanel.userPosts.followers');
      default:
        return t('adminPanel.userPosts.public');
    }
  };

  const handleDeleteConfirm = () => {
    if (postToDelete && deleteReason.trim()) {
      deletePostMutation.mutate({ postId: postToDelete.id, reason: deleteReason });
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              {t('adminPanel.userPosts.title')} - {user?.full_name || user?.email}
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] pr-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : posts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Image className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('adminPanel.userPosts.noPostsFound')}</h3>
                <p className="text-muted-foreground">
                  {t('adminPanel.userPosts.noPostsDescription')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    className="border rounded-lg overflow-hidden hover:border-primary/50 transition-colors"
                  >
                    {/* Media Preview */}
                    <div className="aspect-video bg-muted relative">
                      {post.media_files && post.media_files.length > 0 ? (
                        post.media_files[0].media_type.includes('video') ? (
                          <video
                            src={post.media_files[0].url}
                            controls
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img
                            src={post.media_files[0].url}
                            alt="Post media"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                parent.classList.add('flex', 'items-center', 'justify-center');
                                const icon = document.createElement('div');
                                icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
                                parent.appendChild(icon);
                              }
                            }}
                          />
                        )
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="h-12 w-12 text-muted-foreground" />
                        </div>
                      )}
                      
                      {/* Privacy Badge */}
                      <Badge 
                        variant="secondary" 
                        className="absolute top-2 right-2 flex items-center gap-1"
                      >
                        {getPrivacyIcon(post.privacy_setting)}
                        {getPrivacyLabel(post.privacy_setting)}
                      </Badge>

                      {/* Media count */}
                      {post.media_files && post.media_files.length > 1 && (
                        <Badge 
                          variant="secondary" 
                          className="absolute bottom-2 right-2"
                        >
                          +{post.media_files.length - 1}
                        </Badge>
                      )}
                    </div>

                    {/* Post Info */}
                    <div className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(post.created_at), 'dd MMM yyyy HH:mm')}
                        </span>
                        {post.pets && (
                          <Badge variant="outline" className="text-xs">
                            {post.pets.name}
                          </Badge>
                        )}
                      </div>

                      {post.caption && (
                        <p className="text-sm line-clamp-2">{post.caption}</p>
                      )}

                      <Button
                        variant="destructive"
                        size="sm"
                        className="w-full"
                        onClick={() => setPostToDelete(post)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('adminPanel.userPosts.deletePost')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!postToDelete} onOpenChange={() => setPostToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('adminPanel.userPosts.deletePost')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('adminPanel.userPosts.deletePostConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 py-4">
            <Label htmlFor="deleteReason">{t('adminPanel.userPosts.deleteReason')}</Label>
            <Textarea
              id="deleteReason"
              placeholder={t('adminPanel.userPosts.deleteReasonPlaceholder')}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              rows={3}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setPostToDelete(null);
              setDeleteReason('');
            }}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={!deleteReason.trim() || deletePostMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePostMutation.isPending ? t('adminPanel.userPosts.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default UserPostsViewer;
